export class GuestLogin {
  static readonly type = '[Lobby] Guest Login';
}

export class GuestLoginSuccess {
  static readonly type = '[Lobby] Guest Login Success';
  constructor(public clientId: number, public username: string) {}
}

export class PlayerJoined {
  static readonly type = '[Lobby] Player Joined';
  constructor(public player: { username: string; is_bot: boolean }) {}
}

export class PlayerLeft {
  static readonly type = '[Lobby] Player Left';
  constructor(public username: string) {}
}

export class PlayerListUpdated {
  static readonly type = '[Lobby] Player List Updated';
  constructor(public players: { username: string; is_bot: boolean }[]) {}
}

export class LobbyChatReceived {
  static readonly type = '[Lobby] Chat Received';
  constructor(
    public messageId: string,
    public username: string,
    public message: string,
    public kind: 'chat' | 'logon' | 'logoff' | 'system',
    public timestamp: string,
  ) {}
}

export class SendLobbyChat {
  static readonly type = '[Lobby] Send Chat';
  constructor(public message: string) {}
}

export class ChatHistoryLoaded {
  static readonly type = '[Lobby] Chat History Loaded';
  constructor(
    public messages: Array<{
      message_id: string;
      username: string;
      message: string;
      timestamp: string;
      likes: string[];
      kind: 'chat' | 'logon' | 'logoff' | 'system';
    }>
  ) {}
}

export class AuthLogin {
  static readonly type = '[Lobby] Auth Login';
  constructor(public token: string) {}
}

export class AuthLoginSuccess {
  static readonly type = '[Lobby] Auth Login Success';
  constructor(public clientId: number, public username: string, public displayName: string) {}
}

export class KeycloakLogin {
  static readonly type = '[Lobby] Keycloak Login';
}

export class KeycloakRegister {
  static readonly type = '[Lobby] Keycloak Register';
}

export class AvailableGamesReceived {
  static readonly type = '[Lobby] Available Games Received';
  constructor(public games: string[]) {}
}

export class SendChallenge {
  static readonly type = '[Lobby] Send Challenge';
  constructor(
    public targetUsername: string,
    public game: string,
    public initialTimeSecs: number,
    public incrementSecs: number,
    public rated: boolean,
    public isPublic = true,
    public desiredColor?: string,
  ) {}
}

export class ChallengeSentConfirmed {
  static readonly type = '[Lobby] Challenge Sent Confirmed';
  constructor(
    public challengeId: string,
    public toUsername: string,
    public game: string,
    public initialTimeSecs: number,
    public incrementSecs: number,
    public rated: boolean,
    public isPublic: boolean,
  ) {}
}

export class ChallengeReceived {
  static readonly type = '[Lobby] Challenge Received';
  constructor(
    public challengeId: string,
    public fromUsername: string,
    public game: string,
    public initialTimeSecs: number,
    public incrementSecs: number,
    public rated: boolean,
    public isPublic: boolean,
  ) {}
}

export class AcceptChallenge {
  static readonly type = '[Lobby] Accept Challenge';
  constructor(public challengeId: string) {}
}

export class DeclineChallenge {
  static readonly type = '[Lobby] Decline Challenge';
  constructor(public challengeId: string) {}
}

export class RevokeChallenge {
  static readonly type = '[Lobby] Revoke Challenge';
  constructor(public challengeId: string) {}
}

export class ChallengeDeclined {
  static readonly type = '[Lobby] Challenge Declined';
  constructor(public challengeId: string) {}
}

export class ChallengeRevoked {
  static readonly type = '[Lobby] Challenge Revoked';
  constructor(public challengeId: string) {}
}

export class ChallengeAccepted {
  static readonly type = '[Lobby] Challenge Accepted';
  constructor(public challengeId: string, public game: string, public opponent: string) {}
}

export class MessageLiked {
  static readonly type = '[Lobby] Message Liked';
  constructor(public messageId: string, public likeCount: number) {}
}

export class LikeMessage {
  static readonly type = '[Lobby] Like Message';
  constructor(public messageId: string) {}
}

export interface PlayerSummary {
  seat: number;
  username: string;
}

export interface ActiveGameInfo {
  game_id: string;
  game: string;
  players: PlayerSummary[];
  initial_time_secs: number;
  increment_secs: number;
  started_at: string;
  public: boolean;
  observer_count: number;
  mode?: string;
}

export class ActiveGamesListReceived {
  static readonly type = '[Lobby] Active Games List Received';
  constructor(public games: ActiveGameInfo[]) {}
}

export class ActiveGameAdded {
  static readonly type = '[Lobby] Active Game Added';
  constructor(public game: ActiveGameInfo) {}
}

export class ActiveGameRemoved {
  static readonly type = '[Lobby] Active Game Removed';
  constructor(public gameId: string) {}
}

export class ObserveGame {
  static readonly type = '[Lobby] Observe Game';
  constructor(public gameId: string) {}
}

export class StopObserving {
  static readonly type = '[Lobby] Stop Observing';
  constructor(public gameId: string) {}
}

export class SendGameChat {
  static readonly type = '[Lobby] Send Game Chat';
  constructor(public gameId: string, public message: string) {}
}

export class BlockUser {
  static readonly type = '[Lobby] Block User';
  constructor(public username: string) {}
}

export class UnblockUser {
  static readonly type = '[Lobby] Unblock User';
  constructor(public username: string) {}
}

export class ListBlockedUsers {
  static readonly type = '[Lobby] List Blocked Users';
}

export class BlockedUsersListReceived {
  static readonly type = '[Lobby] Blocked Users List Received';
  constructor(public users: string[]) {}
}

export class StartDemonstration {
  static readonly type = '[Lobby] Start Demonstration';
  constructor(public game: string, public title?: string) {}
}

export class StartReview {
  static readonly type = '[Lobby] Start Review';
  constructor(public gameId: string) {}
}

import { PlayerSeat } from './game.state';

export class DemonstrationStarted {
  static readonly type = '[Lobby] Demonstration Started';
  constructor(public gameId: string, public game: string, public players: PlayerSeat[]) {}
}

export class ListStoredGames {
  static readonly type = '[Lobby] List Stored Games';
  constructor(public mode: string, public limit = 50, public offset = 0) {}
}

export class StoredGamesListReceived {
  static readonly type = '[Lobby] Stored Games List Received';
  constructor(public games: StoredGameInfo[]) {}
}

export interface StoredGameInfo {
  game_id: string;
  mode: string;
  game: string;
  title?: string;
  description?: string;
  players: PlayerSummary[];
  ended_at: string;
  ply_count: number;
}

export class ObserveStoredGame {
  static readonly type = '[Lobby] Observe Stored Game';
  constructor(public gameId: string) {}
}

export interface FriendInfo {
  username: string;
  avatar_url: string | null;
  online: boolean;
}

export interface RequestInfo {
  username: string;
}

export interface VariantStats {
  game: string;
  games_played: number;
  wins: number;
  losses: number;
  draws: number;
}

export interface PlayerStats {
  games_played: number;
  wins: number;
  losses: number;
  draws: number;
  member_since: string;
  per_variant: VariantStats[];
}

export interface ProfileInfo {
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  online: boolean;
  friendship: string;
  blocked_by_me: boolean;
  blocked_me: boolean;
  stats: PlayerStats | null;
  bio: string | null;
  notify_friend_online: boolean | null;
}

export class FriendsListReceived {
  static readonly type = '[Lobby] Friends List Received';
  constructor(public friends: FriendInfo[]) {}
}

export class FriendRequestsListReceived {
  static readonly type = '[Lobby] Friend Requests List Received';
  constructor(public incoming: RequestInfo[], public outgoing: RequestInfo[]) {}
}

export class FriendRequestReceivedNotification {
  static readonly type = '[Lobby] Friend Request Received';
  constructor(public fromUsername: string) {}
}

export class FriendRequestSentNotification {
  static readonly type = '[Lobby] Friend Request Sent';
  constructor(public targetUsername: string) {}
}

export class FriendRequestAcceptedNotification {
  static readonly type = '[Lobby] Friend Request Accepted';
  constructor(public username: string) {}
}

export class FriendRequestDeclinedNotification {
  static readonly type = '[Lobby] Friend Request Declined';
  constructor(public username: string) {}
}

export class FriendRemovedNotification {
  static readonly type = '[Lobby] Friend Removed';
  constructor(public username: string) {}
}

export class FriendOnlineNotification {
  static readonly type = '[Lobby] Friend Online';
  constructor(public username: string) {}
}

export class FriendOfflineNotification {
  static readonly type = '[Lobby] Friend Offline';
  constructor(public username: string) {}
}

export class SendFriendRequest {
  static readonly type = '[Lobby] Send Friend Request';
  constructor(public username: string) {}
}

export class AcceptFriendRequest {
  static readonly type = '[Lobby] Accept Friend Request';
  constructor(public username: string) {}
}

export class DeclineFriendRequest {
  static readonly type = '[Lobby] Decline Friend Request';
  constructor(public username: string) {}
}

export class CancelFriendRequest {
  static readonly type = '[Lobby] Cancel Friend Request';
  constructor(public username: string) {}
}

export class RemoveFriend {
  static readonly type = '[Lobby] Remove Friend';
  constructor(public username: string) {}
}

export class ListFriends {
  static readonly type = '[Lobby] List Friends';
}

export class ListFriendRequests {
  static readonly type = '[Lobby] List Friend Requests';
}

export class GetProfile {
  static readonly type = '[Lobby] Get Profile';
  constructor(public username: string) {}
}

export class SetPlayerListTab {
  static readonly type = '[Lobby] Set Player List Tab';
  constructor(public tab: 'online' | 'friends' | 'requests' | 'blocked') {}
}

export class ProfileReceived {
  static readonly type = '[Lobby] Profile Received';
  constructor(public profile: ProfileInfo) {}
}

export class UpdateProfile {
  static readonly type = '[Lobby] Update Profile';
  constructor(
    public target_username: string,
    public display_name: string | null,
    public bio: string | null,
    public avatar_url: string | null,
    public notify_friend_online: boolean | null,
  ) {}
}

export class ClearProfile {
  static readonly type = '[Lobby] Clear Profile';
}

export class UpdateNotifyFriendOnline {
  static readonly type = '[Lobby] Update Notify Friend Online';
  constructor(public enabled: boolean) {}
}
